import { createPatchData } from 'features/fde/helpers/rolodex/createPatchData'
import { collectDefaultsForFields } from 'features/libra/ontology/ontologyFieldDefaults'
import {
  FieldValues,
  FieldValuesSchema,
  OntologyField,
} from 'features/libra/ontology/ontologyFieldTypes'
import { FormLayoutItem } from 'features/libra/sections/formLayout'
import { Section, SectionType } from 'features/libra/sections/sectionTypes'
import { getVisibleSectionIds } from 'features/libra/stepComponents/conditionalForm/conditionalFormSection/conditionalFormVisibility'
import { useActiveScopedFieldAccess } from 'features/libra/ticket/useScopedFieldAccess'
import { useCallback, useMemo, useState } from 'react'

type UseWorkflowFieldValuesOptions = {
  ontologyFields: Record<string, OntologyField>
  sections: ReadonlyArray<Section>
  form: ReadonlyArray<FormLayoutItem>
}

export const useWorkflowFieldValues = ({
  ontologyFields,
  sections,
  form,
}: UseWorkflowFieldValuesOptions) => {
  // Scope-aware: inside a tabbed step we read/write the scoped
  // `fieldValues.components[componentId]` slice; outside we proxy the
  // top-level ticket surface.
  const access = useActiveScopedFieldAccess()
  const { isLoading } = access
  const ticketFieldValues = access.fieldValues
  const saveFieldValues = access.setFieldValues

  const initialFieldValues = useMemo<FieldValues>(
    () => FieldValuesSchema.parse(ticketFieldValues ?? {}),
    [ticketFieldValues]
  )

  const [localFieldValues, setLocalFieldValues] =
    useState<FieldValues>(initialFieldValues)

  const normalizedFieldValues = useMemo<FieldValues>(
    () => localFieldValues ?? {},
    [localFieldValues]
  )

  // Get all visible section IDs
  const visibleSectionIds = useMemo(
    () => getVisibleSectionIds(form, normalizedFieldValues),
    [form, normalizedFieldValues]
  )

  // Get all visible sections (both regular and flowchart)
  const allVisibleSections = useMemo(
    () =>
      visibleSectionIds
        .map((sectionId) =>
          sections.find((section) => section.id === sectionId)
        )
        .filter((section): section is Section => section !== undefined),
    [visibleSectionIds, sections]
  )

  // Get fields from form sections only
  const visibleFields = useMemo(
    () =>
      allVisibleSections
        .filter((section) => section.type === SectionType.FORM)
        .flatMap((section) => section.fields),
    [allVisibleSections]
  )

  const defaultsForVisibleFields = useMemo(
    () =>
      collectDefaultsForFields(
        normalizedFieldValues,
        ontologyFields,
        visibleFields
      ),
    [normalizedFieldValues, ontologyFields, visibleFields]
  )

  const fieldValuesWithDefaults = useMemo<FieldValues>(() => {
    const merged = {
      ...normalizedFieldValues,
      ...defaultsForVisibleFields,
    }
    return Object.fromEntries(
      Object.entries(merged).filter(([, value]) => value !== undefined)
    ) as FieldValues
  }, [normalizedFieldValues, defaultsForVisibleFields])

  const hasChanges = useMemo(
    () =>
      createPatchData('id', initialFieldValues, fieldValuesWithDefaults) !==
      undefined,
    [fieldValuesWithDefaults, initialFieldValues]
  )

  const handleCancel = useCallback(() => {
    setLocalFieldValues(initialFieldValues)
  }, [initialFieldValues])

  const handleSave = useCallback(async () => {
    await saveFieldValues(fieldValuesWithDefaults as Partial<FieldValues>)
  }, [fieldValuesWithDefaults, saveFieldValues])

  return {
    fieldValues: localFieldValues,
    fieldValuesWithDefaults,
    initialFieldValues,
    setFieldValues: setLocalFieldValues,
    allVisibleSections,
    hasChanges,
    handleCancel,
    handleSave,
    isLoading,
  }
}

export type UseWorkflowFieldValuesReturn = ReturnType<
  typeof useWorkflowFieldValues
>
